import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppDispatch, RootState } from '../store/store';
import {
  fetchSubusers,
  fetchMaxAllowedSubusers,
  createSubuser,
  deleteSubuser,
  updateSubuserPermissions,
  clearError,
} from '../store/slices/subusersSlice';
import { addNotification } from '../store/slices/notificationSlice';
import type { SubuserView } from '../api/generated/models/SubuserView';
import type { CreateSubuserRequest } from '../api/generated/models/CreateSubuserRequest';
import type { PermissionCatalogProductView } from '../api/generated/models/PermissionCatalogProductView';
import { SubusersService } from '../api/generated/services/SubusersService';
import { PermissionsService } from '../api/generated/services/PermissionsService';
import Modal from './ui/Modal';
import Input from './ui/Input';
import robotIllustration from '../assets/img/Illustration-Invoices.png';
import { ReactComponent as EditIcon } from '../assets/icons/edit-contained.svg';
import { ReactComponent as EmailIcon } from '../assets/icons/email.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/delete.svg';
import { OpenAPI } from '../api/generated/core/OpenAPI';
import { Link } from 'react-router-dom';

// --- Permission helpers ---

function getUserProductLabels(
  catalog: PermissionCatalogProductView[],
  permissions: string[]
): string[] {
  const permSet = new Set(permissions);
  return catalog
    .filter(product => {
      const readKey = product.permissions?.find(p => !p.isVisible)?.key;
      return readKey ? permSet.has(readKey) : false;
    })
    .map(product => product.label);
}

function buildPermissions(
  catalog: PermissionCatalogProductView[],
  selectedProducts: Set<string>,
  toggles: Record<string, boolean>
): string[] {
  const keys: string[] = [];
  for (const product of catalog) {
    if (!selectedProducts.has(product.product)) continue;
    for (const perm of product.permissions ?? []) {
      if (!perm.isVisible) {
        keys.push(perm.key); // *_read always included when product is selected
      } else if (toggles[perm.key]) {
        keys.push(perm.key);
      }
    }
  }
  return keys;
}

function initPermissionState(
  catalog: PermissionCatalogProductView[],
  existingPermissions: string[]
): { selectedProducts: Set<string>; toggles: Record<string, boolean> } {
  const permSet = new Set(existingPermissions);
  const selectedProducts = new Set<string>();
  const toggles: Record<string, boolean> = {};
  for (const product of catalog) {
    const readKey = product.permissions?.find(p => !p.isVisible)?.key;
    if (readKey && permSet.has(readKey)) {
      selectedProducts.add(product.product);
    }
    for (const perm of product.permissions ?? []) {
      if (perm.isVisible) {
        toggles[perm.key] = permSet.has(perm.key);
      }
    }
  }
  return { selectedProducts, toggles };
}

// --- Component ---

const UserManagementTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const { subusers, maxAllowed, error, createLoading, deleteLoading, updateLoading, isLoaded } = useSelector(
    (state: RootState) => state.subusers
  );

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<SubuserView | null>(null);
  const [userToEdit, setUserToEdit] = useState<SubuserView | null>(null);
  const [canAddUser, setCanAddUser] = useState(false);
  const [userCreateError, setUserCreateError] = useState<Partial<Record<keyof CreateSubuserRequest, string>>>({});
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null);

  // Permission catalog state
  const [catalog, setCatalog] = useState<PermissionCatalogProductView[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [toggles, setToggles] = useState<Record<string, boolean>>({});

  const fetchCatalog = useCallback(async () => {
    if (catalog.length > 0) return; // already loaded
    setCatalogLoading(true);
    try {
      const data = await PermissionsService.getAppPermissionCatalog();
      setCatalog(data);
    } catch {
      dispatch(addNotification({ type: 'error', message: t('error_loading_catalog') }));
    } finally {
      setCatalogLoading(false);
    }
  }, [catalog.length, dispatch, t]);

  useEffect(() => {
    if (OpenAPI.TOKEN) {
      dispatch(fetchSubusers());
      dispatch(fetchMaxAllowedSubusers());
      fetchCatalog();
    }
  }, [dispatch, fetchCatalog, OpenAPI.TOKEN]);

  useEffect(() => {
    if (error) {
      dispatch(addNotification({ type: 'error', message: t(`${error}`) }));
      dispatch(clearError());
    }
  }, [error, dispatch, t]);

  useEffect(() => {
    if (maxAllowed) {
      setCanAddUser(subusers.length < maxAllowed);
    }
  }, [maxAllowed, subusers.length]);

  const openAddModal = async () => {
    setFormData({ name: '', email: '' });
    setUserCreateError({});
    setSelectedProducts(new Set());
    setToggles({});
    setIsAddModalOpen(true);
    await fetchCatalog();
  };

  const openEditModal = async (user: SubuserView) => {
    setUserToEdit(user);
    setIsEditModalOpen(true);
    await fetchCatalog();
    // init state after catalog is loaded (or already is)
    const currentCatalog = catalog.length > 0 ? catalog : await PermissionsService.getAppPermissionCatalog().then(d => { setCatalog(d); return d; });
    const { selectedProducts: sp, toggles: tg } = initPermissionState(currentCatalog, user.permissions ?? []);
    setSelectedProducts(sp);
    setToggles(tg);
  };

  const handleProductToggle = (productSlug: string) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productSlug)) {
        next.delete(productSlug);
      } else {
        next.add(productSlug);
      }
      return next;
    });
  };

  const handleCapabilityToggle = (key: string) => {
    setToggles(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAddUser = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      setUserCreateError({ name: t('name_error'), email: t('email_error') });
      return;
    }
    if (subusers.length >= maxAllowed) {
      dispatch(addNotification({ type: 'error', message: t('max_users_reached') }));
      return;
    }
    try {
      const permissions = buildPermissions(catalog, selectedProducts, toggles);
      await dispatch(createSubuser({ name: formData.name, email: formData.email, permissions })).unwrap();
      dispatch(addNotification({ type: 'success', message: t('create_user_success') }));
      setIsAddModalOpen(false);
      setFormData({ name: '', email: '' });
      setUserCreateError({});
    } catch {}
  };

  const handleUpdatePermissions = async () => {
    if (!userToEdit) return;
    try {
      const permissions = buildPermissions(catalog, selectedProducts, toggles);
      await dispatch(updateSubuserPermissions({ id: userToEdit.id, permissions })).unwrap();
      dispatch(addNotification({ type: 'success', message: t('update_permissions_success') }));
      setIsEditModalOpen(false);
      setUserToEdit(null);
    } catch {}
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await dispatch(deleteSubuser(userToDelete)).unwrap();
      dispatch(addNotification({ type: 'success', message: t('delete_user_success') }));
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    } catch {}
  };

  const openDeleteModal = (user: SubuserView) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const handleResendInvitation = async (userId: string) => {
    setResendingInvitationId(userId);
    try {
      await SubusersService.postAppSubuserResendInvitation({ id: userId });
      dispatch(addNotification({ type: 'success', message: t('resend_invitation_success') }));
    } catch (error: any) {
      const errorMessage = error?.body?.error || error?.message || t('resend_invitation_error');
      dispatch(addNotification({ type: 'error', message: errorMessage }));
    } finally {
      setResendingInvitationId(null);
    }
  };

  if (!isLoaded) return null;

  const permissionSection = (
    <div className="permission-section">
      <p className="permission-section-label">{t('product_access_label')}</p>
      {catalogLoading && <p className="permission-section-loading">{t('notification_loading')}</p>}
      {catalog.map(product => (
        <div key={product.product} className="product-permission-item">
          <label className="product-checkbox-label">
            <input
              type="checkbox"
              checked={selectedProducts.has(product.product)}
              onChange={() => handleProductToggle(product.product)}
            />
            <span>{product.label}</span>
          </label>
          {selectedProducts.has(product.product) && (
            <div className="capability-toggles">
              {(product.permissions ?? [])
                .filter(p => p.isVisible)
                .map(perm => (
                  <label key={perm.key} className="capability-toggle-label">
                    <input
                      type="checkbox"
                      checked={!!toggles[perm.key]}
                      onChange={() => handleCapabilityToggle(perm.key)}
                    />
                    <span>{perm.label}</span>
                  </label>
                ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="user-management-content">
      <div className="user-management-top">
        <p>{t('user_management_description')}</p>
        <button
          className="btn btn-primary btn-add-user"
          onClick={openAddModal}
          disabled={!canAddUser}
        >
          + {t('add_new_user')}
        </button>
      </div>

      {maxAllowed < 1 && (
        <div className="user-management-warning">
          <h2>{t('user_management_warning_title')}</h2>
          <p>{t('user_management_warning_description')}</p>
          <Link to={`/licenses/buy?plugin=mobile`}>
            <button className="btn btn-primary">{t('user_management_warning_button')}</button>
          </Link>
        </div>
      )}

      {subusers.length === 0 ? (
        maxAllowed >= 1 && (
          <div className="user-management-empty">
            <h2>{t('user_management_empty_title')}</h2>
            <p>{t('user_management_empty_description')}</p>
            <div className="user-management-illustration">
              <img src={robotIllustration} alt="Robot illustration" />
            </div>
          </div>
        )
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('table_user')}</th>
              <th>{t('table_permissions')}</th>
              <th>{t('table_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {subusers.map((user) => (
              <tr key={user.id}>
                <td>
                  <div>{user.name}</div>
                  <div className="table-user-email">{user.email}</div>
                </td>
                <td>{getUserProductLabels(catalog, user.permissions ?? []).join(', ') || '-'}</td>
                <td className="table-actions">
                  <button className="btn-link table-action-btn" onClick={() => openEditModal(user)}>
                    <EditIcon width={14} height={14} />
                    {t('edit_permissions')}
                  </button>
                  <button
                    className="btn-link table-action-btn resend-link"
                    onClick={() => handleResendInvitation(user.id)}
                    disabled={resendingInvitationId === user.id}
                  >
                    <EmailIcon width={14} height={14} />
                    {resendingInvitationId === user.id ? t('notification_loading') : t('resend_invitation')}
                  </button>
                  <button
                    className="btn-link table-action-btn delete-link"
                    onClick={() => openDeleteModal(user)}
                    disabled={deleteLoading}
                  >
                    <DeleteIcon width={13} height={14} />
                    {t('delete_user')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add User Modal */}
      <Modal
        isOpen={isAddModalOpen}
        hideCloseButton={true}
        isCentered={true}
        onClose={() => setIsAddModalOpen(false)}
        title={t('adding_user_modal_title')}
        className="add-subuser-modal"
      >
        <div className="modal-content">
          <div className="input-group subuser-input">
            <Input
              id="email"
              label={t('email_label')}
              placeholder={t('email_subuser_placeholder')}
              type="email"
              value={formData.email}
              error={userCreateError.email}
              required
              onChange={(value) => setFormData({ ...formData, email: value })}
              autoComplete="email"
            />
          </div>
          <div className="input-group subuser-input">
            <Input
              id="name"
              label={t('name_label')}
              placeholder={t('name_subuser_placeholder')}
              type="text"
              value={formData.name}
              error={userCreateError.name}
              required
              onChange={(value) => setFormData({ ...formData, name: value })}
              autoComplete="name"
            />
          </div>
          {permissionSection}
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setIsAddModalOpen(false)}>
              {t('cancel_button')}
            </button>
            <button className="btn btn-primary" onClick={handleAddUser} disabled={createLoading}>
              {t('submit_button')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Permissions Modal */}
      <Modal
        isOpen={isEditModalOpen}
        hideCloseButton={true}
        isCentered={true}
        onClose={() => setIsEditModalOpen(false)}
        title={t('edit_permissions_modal_title')}
        className="add-subuser-modal"
      >
        <div className="modal-content">
          {permissionSection}
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setIsEditModalOpen(false)}>
              {t('cancel_button')}
            </button>
            <button className="btn btn-primary" onClick={handleUpdatePermissions} disabled={updateLoading}>
              {t('submit_button')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        hideCloseButton={true}
        isCentered={true}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t('delete_user_modal_title')}
        className="delete-subuser-modal"
      >
        <div className="modal-content">
          <p className="delete-warning">
            <span className="delete-warning-text">{t('please_note')}</span> {t('delete_user_modal_warning')}
          </p>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setIsDeleteModalOpen(false)}>
              {t('cancel_button')}
            </button>
            <button className="btn btn-danger" onClick={handleDeleteUser} disabled={deleteLoading}>
              {t('delete_user_button')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserManagementTab;
